/**
 * Highlighting the words of a search term in a title (KPR-355).
 *
 * The SERVER decides what matches (the shared free-text rule, keepr-api
 * docs/SCHEMA.md "Free text"); this only shows the person where, in the
 * stored title they are looking at, the words they typed sit. It reads the term
 * as the rule does so the marks agree with the matches: whitespace splits the
 * words, a "quoted phrase" is one word, case and accents are ignored, and no
 * character of the term is special.
 */

/** A character folded for matching: accents dropped, lower case. */
const foldChar = char => char.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

/** The words of a term: split on whitespace outside double quotes; a quoted phrase is one word. */
export function termWords(term) {
	const text = `${term || ''}`;
	const words = [];
	const pattern = /"([^"]+)"|(\S+)/g;
	let match = pattern.exec(text);
	while (match) {
		const word = (match[1] !== undefined ? match[1] : match[2]).trim().replace(/\s+/g, ' ');
		if (word) words.push(word);
		match = pattern.exec(text);
	}
	return words;
}

/** `text` folded, with `map[i]` the index in `text` of folded character i. */
function fold(text) {
	let folded = '';
	const map = [];
	let index = 0;
	for (const char of text) {
		const piece = foldChar(char);
		for (let k = 0; k < piece.length; k += 1) map.push(index);
		folded += piece;
		index += char.length;
	}
	map.push(index);
	return { folded, map };
}

/**
 * The parts of `text`, in order, as `[{ text, mark }]`: the stretches that hold
 * a word of `term` are marked. Overlapping words merge into one mark. A text or
 * term with nothing to mark is one unmarked part.
 */
export function highlightParts(text, term) {
	const source = `${text ?? ''}`;
	const words = termWords(term);
	if (!source || !words.length) return [{ text: source, mark: false }];
	const { folded, map } = fold(source);
	const ranges = [];
	words.forEach((word) => {
		const pieces = fold(word).folded.split(/\s+/).filter(Boolean);
		if (!pieces.length) return;
		// A phrase matches over any run of whitespace; every other character is literal.
		const pattern = new RegExp(pieces.map(piece => piece.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('\\s+'), 'g');
		let hit = pattern.exec(folded);
		while (hit) {
			if (hit[0].length) ranges.push([map[hit.index], map[hit.index + hit[0].length]]);
			else pattern.lastIndex += 1;
			hit = pattern.exec(folded);
		}
	});
	if (!ranges.length) return [{ text: source, mark: false }];
	ranges.sort((a, b) => a[0] - b[0]);
	const merged = [ranges[0].slice()];
	ranges.slice(1).forEach(([start, end]) => {
		const last = merged[merged.length - 1];
		if (start <= last[1]) last[1] = Math.max(last[1], end);
		else merged.push([start, end]);
	});
	const parts = [];
	let cursor = 0;
	merged.forEach(([start, end]) => {
		if (start > cursor) parts.push({ text: source.slice(cursor, start), mark: false });
		parts.push({ text: source.slice(start, end), mark: true });
		cursor = end;
	});
	if (cursor < source.length) parts.push({ text: source.slice(cursor), mark: false });
	return parts;
}
